import React from 'react';
import * as y from 'yup';
import { FormikProps } from 'formik';
import { Row, Col, message } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import { RisingStarActivity, RisingStarActivityInput, ActivityTypes } from 'app/api/generated';
import Upload from 'common/components/Upload';
import InputText from 'common/components/InputText';
import InputColor from 'common/components/InputColor';
import Toggle, { getToggleFieldProps } from 'common/components/Toggle';
import { MAX_ACTIVITY_NAME_LEN } from 'features/program/validations';
import css from 'features/program/Program.module.css';

enum RisingStarArrowTypes {
  Custom = 'custom',
  Default = 'default',
}

const validateTransparentPng = (file: RcFile): Promise<boolean> | boolean => {
  if (file.type !== 'image/png') {
    message.error('Arrow must be a PNG image');
    return false;
  }

  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    const finish = (valid: boolean) => {
      URL.revokeObjectURL(url);
      if (!valid) message.error('Arrow PNG must have a transparent background');
      resolve(valid);
    };

    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 256 / Math.max(image.naturalWidth, image.naturalHeight));
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const context = canvas.getContext('2d');
        if (!context) return finish(false);
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        const pixels = context.getImageData(0, 0, canvas.width, canvas.height).data;
        let transparent = false;
        for (let i = 3; i < pixels.length; i += 4) {
          if (pixels[i] < 255) {
            transparent = true;
            break;
          }
        }
        finish(transparent);
      } catch {
        finish(false);
      }
    };
    image.onerror = () => finish(false);
    image.src = url;
  });
};

const RisingStar = ({ form }: { form: FormikProps<Partial<RisingStarActivity>> }) => {
  return (
    <div style={{ width: 530, maxWidth: '100%' }}>
      <InputText
        {...form.getFieldProps('name')}
        touched={form.touched.name}
        error={form.errors.name}
        label="Activity name"
        maxLength={MAX_ACTIVITY_NAME_LEN}
        showCount
        placeholder="Type your Activity name"
        autoComplete="off"
        className={css.field}
      />
      <InputText
        {...form.getFieldProps('xmlVoteId')}
        touched={form.touched.xmlVoteId}
        error={form.errors.xmlVoteId}
        label="XML vote ID"
        placeholder="XML_name_mako"
        autoComplete="off"
        className={css.field}
      />
      <InputText
        {...form.getFieldProps('singerName')}
        touched={form.touched.singerName}
        error={form.errors.singerName}
        label="Artist name"
        placeholder="לדוגמה: נטע ברזילי"
        autoComplete="off"
        className={css.field}
      />
      <Upload
        {...form.getFieldProps('singerImage')}
        touched={form.touched.singerImage}
        error={form.errors.singerImage}
        accept="image/png, image/jpeg"
        defaultPlaceholder="Image"
        limit={300}
        placeholder={<p>JPG, PNG | 300KB</p>}
        label="Artist image"
        onFileChange={form.setFieldValue}
        className={css.field}
      />
      <InputText
        {...form.getFieldProps('questionText')}
        touched={form.touched.questionText}
        error={form.errors.questionText}
        label="Question text"
        placeholder="האם נטע תרים את המסך?"
        autoComplete="off"
        className={css.field}
      />
      <InputText
        {...form.getFieldProps('songName')}
        touched={form.touched.songName}
        error={form.errors.songName}
        label="Song name"
        placeholder=" השיר של נטע"
        autoComplete="off"
        className={css.field}
      />
      <Toggle
        {...getToggleFieldProps(form.getFieldProps('showFlare'))}
        touched={form.touched.showFlare}
        error={form.errors.showFlare}
        label="Show flare"
        className={css.field}
        onChange={form.setFieldValue}
      />
      <Row gutter={16}>
        <Col span={12}>
          <InputText
            {...form.getFieldProps('qualifiedText')}
            touched={form.touched.qualifiedText}
            error={form.errors.qualifiedText}
            label="Vote passed text"
            placeholder="עברה!"
            autoComplete="off"
            className={css.field}
          />
        </Col>
        <Col span={12}>
          <InputText
            {...form.getFieldProps('disqualifiedText')}
            touched={form.touched.disqualifiedText}
            error={form.errors.disqualifiedText}
            label="Vote not passed text"
            placeholder="לא עברה"
            autoComplete="off"
            className={css.field}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <InputText
            {...form.getFieldProps('thankYou')}
            touched={form.touched.thankYou}
            error={form.errors.thankYou}
            label="Thank you"
            placeholder="תודה שהשתתפתם"
            autoComplete="off"
            className={css.field}
          />
        </Col>
        <Col span={12}>
          <InputText
            {...form.getFieldProps('continue')}
            touched={form.touched.continue}
            error={form.errors.continue}
            label="Continue"
            placeholder="מיד ממשיכים"
            autoComplete="off"
            className={css.field}
          />
        </Col>
      </Row>
      <Toggle
        name="arrows"
        checked={form.values.arrows === RisingStarArrowTypes.Custom}
        label="Arrows"
        inlineValue={['Custom', 'Custom']}
        className={css.field}
        onChange={(_, custom) =>
          form.setFieldValue('arrows', custom ? RisingStarArrowTypes.Custom : RisingStarArrowTypes.Default)
        }
      />
      {form.values.arrows === RisingStarArrowTypes.Custom && (
        <>
          <Row gutter={16}>
            <Col span={12}>
              <Upload
                {...form.getFieldProps('arrowYes')}
                touched={form.touched.arrowYes}
                error={form.errors.arrowYes}
                accept="image/png"
                beforeUpload={validateTransparentPng}
                defaultPlaceholder="Image"
                limit={300}
                placeholder={<p>Transparent PNG | 300KB</p>}
                label="Yes"
                onFileChange={form.setFieldValue}
                className={css.field}
              />
            </Col>
            <Col span={12}>
              <Upload
                {...form.getFieldProps('arrowNo')}
                touched={form.touched.arrowNo}
                error={form.errors.arrowNo}
                accept="image/png"
                beforeUpload={validateTransparentPng}
                defaultPlaceholder="Image"
                limit={300}
                placeholder={<p>Transparent PNG | 300KB</p>}
                label="No"
                onFileChange={form.setFieldValue}
                className={css.field}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Upload
                {...form.getFieldProps('arrowYesUp')}
                touched={form.touched.arrowYesUp}
                error={form.errors.arrowYesUp}
                accept="image/png"
                beforeUpload={validateTransparentPng}
                defaultPlaceholder="Image"
                limit={300}
                placeholder={<p>Transparent PNG | 300KB</p>}
                label="Yes up"
                onFileChange={form.setFieldValue}
                className={css.field}
              />
            </Col>
            <Col span={12}>
              <Upload
                {...form.getFieldProps('arrowNoDown')}
                touched={form.touched.arrowNoDown}
                error={form.errors.arrowNoDown}
                accept="image/png"
                beforeUpload={validateTransparentPng}
                defaultPlaceholder="Image"
                limit={300}
                placeholder={<p>Transparent PNG | 300KB</p>}
                label="No down"
                onFileChange={form.setFieldValue}
                className={css.field}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <InputColor
                {...form.getFieldProps('yesGlowColor')}
                touched={form.touched.yesGlowColor}
                error={form.errors.yesGlowColor}
                label="Yes glow color"
                placeholder="#73E6FA"
                autoComplete="off"
                className={css.field}
                onColorChange={form.setFieldValue}
              />
            </Col>
            <Col span={12}>
              <InputColor
                {...form.getFieldProps('noGlowColor')}
                touched={form.touched.noGlowColor}
                error={form.errors.noGlowColor}
                label="No glow color"
                placeholder="#FF9682"
                autoComplete="off"
                className={css.field}
                onColorChange={form.setFieldValue}
              />
            </Col>
          </Row>
        </>
      )}
      <InputText
        {...form.getFieldProps('finalText')}
        touched={form.touched.finalText}
        error={form.errors.finalText}
        autoComplete="off"
        className={css.field}
        hidden
      />
    </div>
  );
};

export const ValidationSchema: y.SchemaOf<RisingStarActivityInput> = y.object().shape({
  name: y.string().required('Required'),
  arrows: y.string().oneOf(Object.values(RisingStarArrowTypes)).optional().nullable(),
  arrowYes: y.string().optional().nullable().default(''),
  arrowNo: y.string().optional().nullable().default(''),
  arrowYesUp: y.string().optional().nullable().default(''),
  arrowNoDown: y.string().optional().nullable().default(''),
  yesGlowColor: y.string().optional().nullable().default(''),
  noGlowColor: y.string().optional().nullable().default(''),
  singerName: y.string().required('Required'),
  singerImage: y.string().required('Required'),
  songName: y.string().required('Required'),
  questionText: y.string().required('Required'),
  qualifiedText: y.string().default(''),
  disqualifiedText: y.string().default(''),
  xmlVoteId: y.string().required(),
  finalText: y.string().min(0).default(''),
  thankYou: y.string().optional().nullable().default(''),
  continue: y.string().optional().nullable().default(''),
  showFlare: y.boolean().optional().nullable(),
});

export const initialValues: Partial<RisingStarActivity> = {
  type: ActivityTypes.RisingStar,
  arrows: RisingStarArrowTypes.Default,
  finalText: '',
  qualifiedText: '',
  disqualifiedText: '',
  thankYou: '',
  continue: '',
  showFlare: false,
};

export default RisingStar;
